import type { Chef } from '../types/chef';
import { IMG } from './images';

const verifiedSet = (extra?: { background?: boolean; insurancePending?: boolean }) => [
  {
    kind: 'identity' as const,
    label: 'Thai ID / passport',
    detail: 'Name and photo matched to a government document.',
    status: 'verified' as const,
    verifiedOn: '2026-02-11',
  },
  {
    kind: 'hygiene' as const,
    label: 'Food handler certificate',
    detail: 'Bangkok Metropolitan Administration food handler training, valid through 2028.',
    status: 'verified' as const,
    verifiedOn: '2026-01-28',
  },
  {
    kind: 'insurance' as const,
    label: 'Liability insurance',
    detail: extra?.insurancePending
      ? 'Policy uploaded, waiting on the insurer to confirm coverage dates.'
      : '฿5,000,000 general liability, covers cooking in a client home.',
    status: extra?.insurancePending ? ('pending' as const) : ('verified' as const),
    verifiedOn: extra?.insurancePending ? undefined : '2026-02-02',
  },
  {
    kind: 'background' as const,
    label: 'Police background check',
    detail: extra?.background === false ? 'Not requested yet — optional for chefs.' : 'Royal Thai Police clearance on file.',
    status: extra?.background === false ? ('missing' as const) : ('verified' as const),
    verifiedOn: extra?.background === false ? undefined : '2026-01-19',
  },
];

export const chefs: Chef[] = [
  {
    id: 'chef-elena',
    slug: 'ploy-siriwan',
    name: 'Ploy Siriwan',
    photo: IMG.chefPloy,
    headline: 'Modern Thai tasting menus, pounded and grilled in your kitchen',
    bio: 'I cooked six years at a one-star kitchen in Bangkok and grew up in my grandmother’s curry shop in Nakhon Pathom. I pound every paste in a granite mortar on your counter — no jars, no shortcuts — and I set the chilli to whatever your table can actually take. I bring my own mortar, wok and charcoal tao.',
    cuisineIds: ['thai', 'northern-thai', 'pastry'],
    languages: ['Thai', 'English'],
    fulfillment: ['in_home', 'delivery'],
    city: 'Bangkok',
    neighbourhood: 'Thong Lo',
    lat: 13.7245,
    lng: 100.585,
    rating: 4.9,
    reviewCount: 128,
    bookingsCompleted: 214,
    responseMinutes: 34,
    acceptanceRate: 92,
    repeatRate: 41,
    bookingMode: 'request',
    yearsPro: 12,
    joinedOn: '2023-04-18',
    nextAvailable: '2026-09-06',
    minimumSpend: 4800,
    travelIncludedKm: 8,
    travelPerKm: 25,
    depositPercent: 30,
    cancellationTier: 'moderate',
    dietaryCapabilities: ['Chilli level to order', 'No fish sauce or shrimp paste', 'Gluten-free menus', 'Allergen-separated prep'],
    deliveryFee: 250,
    deliveryFreeRadiusKm: 5,
    minDeliveryOrder: 2500,
    menus: [
      {
        id: 'menu-elena-sunday',
        name: 'Baan Ploy tasting',
        tagline: 'Six courses, one long braise, everything pounded on the day',
        cuisineIds: ['thai'],
        fulfillment: ['in_home'],
        pricePerGuest: 2400,
        minGuests: 4,
        includedHours: 4,
        courses: [
          { name: 'Miang', description: 'Betel leaf with pomelo, toasted coconut and dried shrimp' },
          { name: 'Yam', description: 'Charred aubergine salad, salted egg, crispy shallot' },
          { name: 'Gaeng', description: 'Southern yellow curry with blue crab and betel leaves' },
          { name: 'Yang', description: 'Charcoal-grilled sea bass, nam jim seafood' },
          { name: 'Pad', description: 'Wok-fried morning glory and jasmine rice' },
          { name: 'Khong wan', description: 'Mango sticky rice with toasted mung bean' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishCurry,
        equipmentNeeded: ['wok_burner', 'wok', 'mortar', 'sharp_knives', 'rice_cooker'],
        dietaryTags: ['Chilli to order', 'Pescatarian on request'],
        leadTimeDays: 4,
      },
      {
        id: 'menu-elena-weeknight',
        name: 'Weeknight Thai table',
        tagline: 'Four sharing dishes and rice, on the table in two hours',
        cuisineIds: ['thai'],
        fulfillment: ['in_home', 'delivery'],
        pricePerGuest: 1150,
        pricePerPortion: 420,
        minGuests: 2,
        includedHours: 2,
        courses: [
          { name: 'Soup', description: 'Tom kha with young coconut and chicken' },
          { name: 'Curry', description: 'Green curry, Thai aubergine, holy basil' },
          { name: 'Stir-fry', description: 'Pad krapow with a crisp fried egg' },
          { name: 'Salad', description: 'Som tam with long bean and peanut on the side' },
        ],
        groceries: 'at_cost',
        signature: false,
        image: IMG.dishIsan,
        equipmentNeeded: ['wok', 'wok_burner', 'mortar', 'rice_cooker'],
        dietaryTags: ['Vegetarian', 'Nut-free on request'],
        leadTimeDays: 2,
      },
      {
        id: 'menu-elena-prep',
        name: 'Week of home-style Thai',
        tagline: 'Five dinners cooked at mine, delivered chilled with heating notes',
        cuisineIds: ['thai'],
        fulfillment: ['delivery'],
        pricePerGuest: 650,
        pricePerPortion: 320,
        minGuests: 1,
        includedHours: 0,
        courses: [
          { name: 'Five mains', description: 'Rotating curries, braises and one clear soup' },
          { name: 'Two sides', description: 'Stir-fried greens and a fresh salad' },
        ],
        groceries: 'included',
        signature: false,
        image: IMG.dishMango,
        equipmentNeeded: [],
        dietaryTags: ['Dairy-free', 'Chilli to order'],
        leadTimeDays: 3,
      },
    ],
    addOns: [
      { id: 'elena-hour', name: 'Extra service hour', description: 'Longer table, more courses, slower night.', price: 1200, unit: 'per_hour' },
      { id: 'elena-wine', name: 'Wine & cocktail pairing', description: 'Three pours per guest, matched to the heat.', price: 950, unit: 'per_guest' },
      { id: 'elena-course', name: 'Extra course', description: 'A seventh plate — usually a raw seafood yam.', price: 380, unit: 'per_guest' },
      { id: 'elena-clean', name: 'Deep kitchen reset', description: 'Full clean-down including hob, hood and floors.', price: 1500, unit: 'flat' },
      { id: 'elena-travel', name: 'Travel beyond area', description: 'Charged per km past 8 km.', price: 25, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'ea1', name: 'Sukhumvit mid', districts: ['Thong Lo', 'Ekkamai', 'Phrom Phong', 'Asok'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.7245, lng: 100.585, radiusKm: 4 },
      { id: 'ea2', name: 'Sathorn & Silom', districts: ['Sathorn', 'Silom', 'Lumphini'], fulfillment: ['in_home'], travelIncluded: true, lat: 13.722, lng: 100.529, radiusKm: 3.5 },
      { id: 'ea3', name: 'Riverside', districts: ['Charoenkrung', 'Khlong San'], fulfillment: ['in_home'], travelIncluded: false, lat: 13.7135, lng: 100.5115, radiusKm: 3 },
    ],
    gallery: [
      { id: 'eg1', url: IMG.dishCurry, caption: 'Southern crab curry with betel leaves', tag: 'dish' },
      { id: 'eg2', url: IMG.heroTable, caption: 'A Sunday table in Thong Lo', tag: 'table' },
      { id: 'eg3', url: IMG.dishMango, caption: 'Mango sticky rice, toasted mung bean', tag: 'dish' },
      { id: 'eg4', url: IMG.cooking, caption: 'Searing in a client kitchen', tag: 'process' },
      { id: 'eg5', url: IMG.dishIsan, caption: 'Som tam pounded to order', tag: 'dish' },
    ],
    verifications: verifiedSet(),
  },
  {
    id: 'chef-kenji',
    slug: 'kenji-watanabe',
    name: 'Kenji Watanabe',
    photo: IMG.chefKenji,
    headline: 'Quiet kaiseki-style dinners for four to eight people',
    bio: 'Twenty years between Kyoto and Bangkok, the last six cooking privately in Sukhumvit. Fish arrives from Tsukiji twice a week and I work in courses that follow the season. I need very little: a clean surface, cold water, and time.',
    cuisineIds: ['japanese'],
    languages: ['Japanese', 'English', 'Thai'],
    fulfillment: ['in_home'],
    city: 'Bangkok',
    neighbourhood: 'Sukhumvit 31',
    lat: 13.737,
    lng: 100.569,
    rating: 5.0,
    reviewCount: 74,
    bookingsCompleted: 96,
    responseMinutes: 96,
    acceptanceRate: 78,
    repeatRate: 52,
    bookingMode: 'request',
    yearsPro: 20,
    joinedOn: '2024-01-09',
    nextAvailable: '2026-09-19',
    minimumSpend: 12000,
    travelIncludedKm: 6,
    travelPerKm: 40,
    depositPercent: 40,
    cancellationTier: 'strict',
    dietaryCapabilities: ['Allergen-separated prep', 'Low-sodium'],
    deliveryFee: 0,
    deliveryFreeRadiusKm: 0,
    minDeliveryOrder: 0,
    menus: [
      {
        id: 'menu-kenji-omakase',
        name: 'Counter omakase, eight courses',
        tagline: 'Fish flown in that morning, served one plate at a time',
        cuisineIds: ['japanese'],
        fulfillment: ['in_home'],
        pricePerGuest: 5500,
        minGuests: 2,
        includedHours: 4,
        courses: [
          { name: 'Sakizuke', description: 'Chilled tofu, dashi jelly, wasabi leaf' },
          { name: 'Otsukuri', description: 'Three sashimi cuts from the morning market' },
          { name: 'Yakimono', description: 'Charcoal-grilled black cod, saikyo miso' },
          { name: 'Nimono', description: 'Simmered daikon, yuzu, kombu' },
          { name: 'Sushi', description: 'Seven pieces, aged rice, served in sequence' },
          { name: 'Mizumono', description: 'Hojicha custard, black sugar' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishMezze,
        equipmentNeeded: ['sharp_knives', 'blowtorch', 'thermometer'],
        dietaryTags: ['Pescatarian'],
        leadTimeDays: 7,
      },
      {
        id: 'menu-kenji-donburi',
        name: 'Donburi dinner for the family',
        tagline: 'Three bowls, pickles and miso — relaxed, still precise',
        cuisineIds: ['japanese'],
        fulfillment: ['in_home'],
        pricePerGuest: 1800,
        minGuests: 2,
        includedHours: 3,
        courses: [
          { name: 'Small plates', description: 'Cucumber sunomono, edamame, house pickles' },
          { name: 'Bowls', description: 'Choice of unagi, chicken katsu, or vegetable tempura' },
          { name: 'Soup', description: 'Miso with clams' },
        ],
        groceries: 'at_cost',
        signature: false,
        image: IMG.dishPasta,
        equipmentNeeded: ['wok_burner', 'sharp_knives', 'steamer', 'rice_cooker'],
        dietaryTags: ['Vegetarian on request'],
        leadTimeDays: 4,
      },
    ],
    addOns: [
      { id: 'kenji-hour', name: 'Extra service hour', description: 'Slower pacing between courses.', price: 2200, unit: 'per_hour' },
      { id: 'kenji-sake', name: 'Sake flight', description: 'Four pours, matched to the courses.', price: 1400, unit: 'per_guest' },
      { id: 'kenji-service', name: 'Second pair of hands', description: 'An assistant to plate and clear.', price: 5500, unit: 'flat' },
      { id: 'kenji-travel', name: 'Travel beyond area', description: 'Charged per km past 6 km.', price: 40, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'ka1', name: 'Lower Sukhumvit', districts: ['Sukhumvit 31', 'Asok', 'Phrom Phong'], fulfillment: ['in_home'], travelIncluded: true, lat: 13.737, lng: 100.569, radiusKm: 3 },
      { id: 'ka2', name: 'Ratchadamri & Lumphini', districts: ['Langsuan', 'Lumphini', 'Chidlom'], fulfillment: ['in_home'], travelIncluded: true, lat: 13.7385, lng: 100.5435, radiusKm: 3 },
    ],
    gallery: [
      { id: 'kg1', url: IMG.dishMezze, caption: 'Otsukuri, three cuts', tag: 'dish' },
      { id: 'kg2', url: IMG.cooking, caption: 'Charcoal, at the counter', tag: 'process' },
      { id: 'kg3', url: IMG.dishDessert, caption: 'Hojicha custard', tag: 'dish' },
      { id: 'kg4', url: IMG.heroTable, caption: 'Eight seats, one sequence', tag: 'table' },
    ],
    verifications: verifiedSet(),
  },
  {
    id: 'chef-amara',
    slug: 'anan-thepsuwan',
    name: 'Anan Thepsuwan',
    photo: IMG.chefAnan,
    headline: 'Isan cooking over charcoal — som tam pounded to your heat',
    bio: 'Udon Thani born, Bangkok trained. I cook over charcoal wherever the building lets me: grilled pork neck, sticky rice steamed in a basket, and som tam pounded in front of you so you can stop me at the right number of chillies. Very calm about allergies — my brother is allergic to peanuts, so a separate mortar has always been normal to me.',
    cuisineIds: ['isan', 'bbq', 'northern-thai'],
    languages: ['Thai', 'Lao', 'English'],
    fulfillment: ['in_home', 'delivery'],
    city: 'Bangkok',
    neighbourhood: 'Ari',
    lat: 13.7797,
    lng: 100.5416,
    rating: 4.8,
    reviewCount: 191,
    bookingsCompleted: 302,
    responseMinutes: 18,
    acceptanceRate: 96,
    repeatRate: 38,
    bookingMode: 'instant',
    yearsPro: 9,
    joinedOn: '2022-11-02',
    nextAvailable: '2026-09-04',
    minimumSpend: 3600,
    travelIncludedKm: 10,
    travelPerKm: 20,
    depositPercent: 25,
    cancellationTier: 'flexible',
    dietaryCapabilities: ['Chilli level to order', 'Nut-free kitchen practice', 'Halal sourcing', 'Jay (Buddhist vegan) menus'],
    deliveryFee: 200,
    deliveryFreeRadiusKm: 6,
    minDeliveryOrder: 1800,
    menus: [
      {
        id: 'menu-amara-party',
        name: 'Isan feast over charcoal',
        tagline: 'Grilled everything, sticky rice, and a table that keeps refilling',
        cuisineIds: ['isan'],
        fulfillment: ['in_home', 'delivery'],
        pricePerGuest: 1250,
        pricePerPortion: 480,
        minGuests: 6,
        includedHours: 3,
        courses: [
          { name: 'To start', description: 'Sai krok Isan, fried pork rind, fresh herbs' },
          { name: 'Grill', description: 'Kor moo yang and grilled tilapia in salt crust' },
          { name: 'Pounded', description: 'Som tam thai and som tam pu pla ra, made to order' },
          { name: 'Sides', description: 'Larb moo, nam tok, sticky rice baskets' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishIsan,
        equipmentNeeded: ['charcoal_grill', 'mortar', 'sticky_steamer', 'sharp_knives'],
        dietaryTags: ['Halal on request', 'Nut-free', 'Gluten-free'],
        leadTimeDays: 3,
      },
      {
        id: 'menu-amara-plant',
        name: 'Jay Isan table',
        tagline: 'Buddhist vegan, no alliums, nothing apologetic',
        cuisineIds: ['isan', 'plant'],
        fulfillment: ['in_home', 'delivery'],
        pricePerGuest: 980,
        pricePerPortion: 380,
        minGuests: 2,
        includedHours: 2.5,
        courses: [
          { name: 'Starter', description: 'Grilled mushroom larb with toasted rice' },
          { name: 'Main', description: 'Jay curry with young jackfruit and holy basil' },
          { name: 'Side', description: 'Som tam with salted plum, sticky rice' },
        ],
        groceries: 'at_cost',
        signature: false,
        image: IMG.dishCurry,
        equipmentNeeded: ['mortar', 'large_stockpot', 'sticky_steamer'],
        dietaryTags: ['Jay', 'Vegan', 'Nut-free'],
        leadTimeDays: 2,
      },
      {
        id: 'menu-amara-lunchbox',
        name: 'Five-day lunch drop',
        tagline: 'Cooked at mine on Sunday, delivered in reheatable trays',
        cuisineIds: ['isan'],
        fulfillment: ['delivery'],
        pricePerGuest: 480,
        pricePerPortion: 240,
        minGuests: 1,
        includedHours: 0,
        courses: [
          { name: 'Five lunches', description: 'Rotating grilled meats, larb and greens' },
          { name: 'Extras', description: 'Jaew sauce and sticky rice on the side' },
        ],
        groceries: 'included',
        signature: false,
        image: IMG.dishMango,
        equipmentNeeded: [],
        dietaryTags: ['Halal on request', 'Nut-free'],
        leadTimeDays: 2,
      },
    ],
    addOns: [
      { id: 'amara-hour', name: 'Extra service hour', description: 'Stay through dessert and clear-down.', price: 900, unit: 'per_hour' },
      { id: 'amara-server', name: 'Server for the night', description: 'Someone to pass plates and pour.', price: 3500, unit: 'flat' },
      { id: 'amara-drinks', name: 'Nam manao & cha yen bar', description: 'Two house drinks, non-alcoholic.', price: 280, unit: 'per_guest' },
      { id: 'amara-fire', name: 'Charcoal setup', description: 'Tao, charcoal and a safety kit for balconies.', price: 1200, unit: 'flat' },
      { id: 'amara-travel', name: 'Travel beyond area', description: 'Charged per km past 10 km.', price: 20, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'aa1', name: 'Phaya Thai & Ari', districts: ['Ari', 'Saphan Khwai', 'Victory Monument'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.7797, lng: 100.5416, radiusKm: 4 },
      { id: 'aa2', name: 'Chatuchak & Lat Phrao', districts: ['Chatuchak', 'Lat Phrao', 'Ratchada'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.8, lng: 100.5615, radiusKm: 4 },
      { id: 'aa3', name: 'Sukhumvit mid', districts: ['Asok', 'Phrom Phong', 'Thong Lo'], fulfillment: ['delivery'], travelIncluded: false, lat: 13.7305, lng: 100.5695, radiusKm: 3.5 },
    ],
    gallery: [
      { id: 'ag1', url: IMG.dishIsan, caption: 'Grilled pork neck and sticky rice', tag: 'dish' },
      { id: 'ag2', url: IMG.cooking, caption: 'Over charcoal on a Bangkok balcony', tag: 'process' },
      { id: 'ag3', url: IMG.heroTable, caption: 'A twelve-person table in Ari', tag: 'table' },
      { id: 'ag4', url: IMG.dishCurry, caption: 'Jay curry with jackfruit', tag: 'dish' },
    ],
    verifications: verifiedSet(),
  },
  {
    id: 'chef-tomas',
    slug: 'somchai-boonmee',
    name: 'Somchai Boonmee',
    photo: IMG.chefSomchai,
    headline: 'Southern Thai seafood and Mediterranean grilling, groceries at cost',
    bio: 'Thirty years of cooking, eleven of them in Phuket and four on a grill station in Spain. I shop Khlong Toei market the morning of, cook whole fish over embers, and show you every receipt. Southern heat as standard, but I will pull it back if you ask.',
    cuisineIds: ['southern-thai', 'bbq', 'levantine'],
    languages: ['Thai', 'English', 'Spanish'],
    fulfillment: ['in_home'],
    city: 'Bangkok',
    neighbourhood: 'Charoenkrung',
    lat: 13.7135,
    lng: 100.5115,
    rating: 4.7,
    reviewCount: 63,
    bookingsCompleted: 88,
    responseMinutes: 142,
    acceptanceRate: 71,
    repeatRate: 29,
    bookingMode: 'request',
    yearsPro: 16,
    joinedOn: '2024-06-21',
    nextAvailable: '2026-09-12',
    minimumSpend: 14000,
    travelIncludedKm: 12,
    travelPerKm: 18,
    depositPercent: 30,
    cancellationTier: 'moderate',
    dietaryCapabilities: ['Gluten-free menus', 'Chilli level to order'],
    deliveryFee: 0,
    deliveryFreeRadiusKm: 0,
    minDeliveryOrder: 0,
    menus: [
      {
        id: 'menu-tomas-parrilla',
        name: 'Whole fish over embers',
        tagline: 'Market seafood, charcoal, and one very good salsa verde',
        cuisineIds: ['southern-thai', 'bbq'],
        fulfillment: ['in_home'],
        pricePerGuest: 1950,
        minGuests: 6,
        includedHours: 4,
        courses: [
          { name: 'To start', description: 'Grilled prawns, nam jim seafood, charred flatbread' },
          { name: 'From the grill', description: 'Whole sea bass in salt with turmeric and lime' },
          { name: 'Second grill', description: 'Massaman-rubbed short rib, piquillo peppers' },
          { name: 'Dessert', description: 'Burnt coconut custard' },
        ],
        groceries: 'at_cost',
        signature: true,
        image: IMG.dishDessert,
        equipmentNeeded: ['charcoal_grill', 'heavy_skillet', 'thermometer', 'sharp_knives'],
        dietaryTags: ['Gluten-free on request'],
        leadTimeDays: 5,
      },
      {
        id: 'menu-tomas-paella',
        name: 'Southern curry feast',
        tagline: 'Gaeng som, khua kling and crab, cooked hot and shared',
        cuisineIds: ['southern-thai'],
        fulfillment: ['in_home'],
        pricePerGuest: 1400,
        minGuests: 8,
        includedHours: 3,
        courses: [
          { name: 'To start', description: 'Stink bean stir-fry, salted egg, herb plate' },
          { name: 'Curries', description: 'Gaeng som pla and khua kling moo' },
          { name: 'Rice & sides', description: 'Turmeric rice, fried mackerel, budu dressing' },
        ],
        groceries: 'estimate',
        groceryEstimatePerGuest: 220,
        signature: false,
        image: IMG.dishCurry,
        equipmentNeeded: ['wok_burner', 'large_stockpot', 'mortar'],
        dietaryTags: ['Pescatarian option', 'Very spicy as standard'],
        leadTimeDays: 4,
      },
    ],
    addOns: [
      { id: 'tomas-hour', name: 'Extra service hour', description: 'These lunches tend to run long.', price: 1100, unit: 'per_hour' },
      { id: 'tomas-wine', name: 'Wine & sherry pairing', description: 'Three pours per guest, chosen for chilli.', price: 850, unit: 'per_guest' },
      { id: 'tomas-shop', name: 'Market trip with me', description: 'Khlong Toei at 6am, then we cook it.', price: 1800, unit: 'flat' },
      { id: 'tomas-travel', name: 'Travel beyond area', description: 'Charged per km past 12 km.', price: 18, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'ta1', name: 'Riverside & Bang Rak', districts: ['Charoenkrung', 'Talat Noi', 'Sathorn'], fulfillment: ['in_home'], travelIncluded: true, lat: 13.7135, lng: 100.5115, radiusKm: 4.5 },
      { id: 'ta2', name: 'Thonburi', districts: ['Khlong San', 'Wongwian Yai'], fulfillment: ['in_home'], travelIncluded: false, lat: 13.7205, lng: 100.4915, radiusKm: 3 },
    ],
    gallery: [
      { id: 'tg1', url: IMG.dishDessert, caption: 'Burnt coconut custard', tag: 'dish' },
      { id: 'tg2', url: IMG.cooking, caption: 'Embers, always embers', tag: 'process' },
      { id: 'tg3', url: IMG.dishCurry, caption: 'Gaeng som, southern heat', tag: 'dish' },
    ],
    verifications: verifiedSet({ insurancePending: true }),
  },
  {
    id: 'chef-priya',
    slug: 'priya-raman',
    name: 'Priya Raman',
    photo: IMG.chefPriya,
    headline: 'South Indian home cooking, spice level set by you',
    bio: 'I grew up between Chennai and Bangkok’s Little India, and I cook the food of Tamil Nadu and Kerala the way my grandmother did — masalas ground the same morning, coconut, curry leaf, nothing from a jar. I ask about heat tolerance before anything else and I am happy cooking entirely vegetarian.',
    cuisineIds: ['indian', 'plant'],
    languages: ['Tamil', 'English', 'Thai'],
    fulfillment: ['in_home', 'delivery'],
    city: 'Bangkok',
    neighbourhood: 'Silom',
    lat: 13.728,
    lng: 100.534,
    rating: 4.9,
    reviewCount: 87,
    bookingsCompleted: 141,
    responseMinutes: 52,
    acceptanceRate: 88,
    repeatRate: 46,
    bookingMode: 'request',
    yearsPro: 7,
    joinedOn: '2024-09-14',
    nextAvailable: '2026-09-05',
    minimumSpend: 3200,
    travelIncludedKm: 9,
    travelPerKm: 20,
    depositPercent: 25,
    cancellationTier: 'flexible',
    dietaryCapabilities: ['Jay (Buddhist vegan) menus', 'Gluten-free menus', 'Nut-free kitchen practice', 'Low-sodium', 'Chilli level to order'],
    deliveryFee: 180,
    deliveryFreeRadiusKm: 6,
    minDeliveryOrder: 1600,
    menus: [
      {
        id: 'menu-priya-thali',
        name: 'South Indian thali night',
        tagline: 'Seven small dishes, one banana leaf, endless rice',
        cuisineIds: ['indian'],
        fulfillment: ['in_home'],
        pricePerGuest: 1100,
        minGuests: 2,
        includedHours: 3,
        courses: [
          { name: 'To begin', description: 'Rasam, appalam, three chutneys' },
          { name: 'The thali', description: 'Sambar, avial, poriyal, kootu, curd rice' },
          { name: 'Bread', description: 'Fresh appam and idiyappam' },
          { name: 'Sweet', description: 'Payasam with jaggery and cardamom' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishPasta,
        equipmentNeeded: ['blender', 'large_stockpot', 'steamer', 'rice_cooker'],
        dietaryTags: ['Vegetarian', 'Vegan on request', 'Gluten-free'],
        leadTimeDays: 3,
      },
      {
        id: 'menu-priya-biryani',
        name: 'Biryani and kebabs',
        tagline: 'Dum-cooked biryani sealed at your table',
        cuisineIds: ['indian'],
        fulfillment: ['in_home', 'delivery'],
        pricePerGuest: 950,
        pricePerPortion: 350,
        minGuests: 2,
        includedHours: 3,
        courses: [
          { name: 'Starters', description: 'Chicken 65 and paneer tikka' },
          { name: 'Main', description: 'Dum biryani, mutton or vegetable' },
          { name: 'Sides', description: 'Raita, mirchi ka salan, onion salad' },
        ],
        groceries: 'estimate',
        groceryEstimatePerGuest: 180,
        signature: false,
        image: IMG.dishMezze,
        equipmentNeeded: ['oven', 'large_stockpot'],
        dietaryTags: ['Halal on request', 'Nut-free'],
        leadTimeDays: 3,
      },
    ],
    addOns: [
      { id: 'priya-hour', name: 'Extra service hour', description: 'More courses, or a slower table.', price: 850, unit: 'per_hour' },
      { id: 'priya-chai', name: 'Filter coffee & chai service', description: 'Made to order after dessert.', price: 190, unit: 'per_guest' },
      { id: 'priya-cook', name: 'Cook-along for two', description: 'Learn one masala properly while I work.', price: 2400, unit: 'flat' },
      { id: 'priya-travel', name: 'Travel beyond area', description: 'Charged per km past 9 km.', price: 20, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'pa1', name: 'Silom & Sathorn', districts: ['Silom', 'Sathorn', 'Surawong', 'Lumphini'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.728, lng: 100.534, radiusKm: 4 },
      { id: 'pa2', name: 'Sukhumvit mid', districts: ['Asok', 'Phrom Phong'], fulfillment: ['in_home'], travelIncluded: false, lat: 13.7305, lng: 100.5695, radiusKm: 3 },
    ],
    gallery: [
      { id: 'pg1', url: IMG.dishPasta, caption: 'Thali, seven dishes', tag: 'dish' },
      { id: 'pg2', url: IMG.cooking, caption: 'Tempering curry leaf', tag: 'process' },
      { id: 'pg3', url: IMG.dishDessert, caption: 'Payasam', tag: 'dish' },
      { id: 'pg4', url: IMG.heroTable, caption: 'Banana leaf service', tag: 'table' },
    ],
    verifications: verifiedSet({ background: false }),
  },
  {
    id: 'chef-marcus',
    slug: 'mei-lin-chow',
    name: 'Mei Lin Chow',
    photo: IMG.chefMei,
    headline: 'Teochew banquets and dim sum, cooked at yours or delivered hot',
    bio: 'Third-generation Bangkok Chinese, trained in Hong Kong. I braise, steam and roast — a proper Teochew banquet with the soup that takes six hours, or dim sum folded at your counter on a Sunday morning. Cheerful about big numbers of people.',
    cuisineIds: ['cantonese', 'peranakan'],
    languages: ['Thai', 'Teochew', 'Mandarin', 'English'],
    fulfillment: ['in_home', 'delivery'],
    city: 'Bangkok',
    neighbourhood: 'Yaowarat',
    lat: 13.7405,
    lng: 100.5095,
    rating: 4.6,
    reviewCount: 152,
    bookingsCompleted: 268,
    responseMinutes: 26,
    acceptanceRate: 94,
    repeatRate: 33,
    bookingMode: 'instant',
    yearsPro: 8,
    joinedOn: '2023-08-30',
    nextAvailable: '2026-09-04',
    minimumSpend: 11000,
    travelIncludedKm: 12,
    travelPerKm: 18,
    depositPercent: 20,
    cancellationTier: 'flexible',
    dietaryCapabilities: ['Gluten-free menus', 'Allergen-separated prep', 'No fish sauce or shrimp paste'],
    deliveryFee: 220,
    deliveryFreeRadiusKm: 7,
    minDeliveryOrder: 2800,
    menus: [
      {
        id: 'menu-marcus-smoke',
        name: 'Teochew banquet',
        tagline: 'Eight dishes, one six-hour soup, carved at your table',
        cuisineIds: ['cantonese'],
        fulfillment: ['in_home', 'delivery'],
        pricePerGuest: 1850,
        pricePerPortion: 620,
        minGuests: 8,
        includedHours: 3,
        courses: [
          { name: 'Cold plate', description: 'Braised goose, tofu, chilli vinegar' },
          { name: 'Soup', description: 'Six-hour pork and lotus root' },
          { name: 'Steamed', description: 'Whole seabass, ginger and scallion' },
          { name: 'Roast', description: 'Crisp pork belly and soy chicken' },
          { name: 'Rice', description: 'Olive fried rice, greens in garlic' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishMezze,
        equipmentNeeded: ['steamer', 'wok', 'wok_burner', 'sharp_knives', 'large_stockpot'],
        dietaryTags: ['Gluten-free on request'],
        leadTimeDays: 3,
      },
      {
        id: 'menu-marcus-taco',
        name: 'Dim sum brunch',
        tagline: 'Folded at your counter, steamed in batches all morning',
        cuisineIds: ['cantonese'],
        fulfillment: ['in_home'],
        pricePerGuest: 890,
        minGuests: 6,
        includedHours: 2.5,
        courses: [
          { name: 'Steamed', description: 'Har gow, siu mai, char siu bao' },
          { name: 'Fried', description: 'Turnip cake, spring rolls, sesame balls' },
          { name: 'Congee', description: 'Century egg and pork, condiments on the side' },
        ],
        groceries: 'at_cost',
        signature: false,
        image: IMG.dishPasta,
        equipmentNeeded: ['steamer', 'wok', 'wok_burner'],
        dietaryTags: ['Vegetarian option'],
        leadTimeDays: 2,
      },
    ],
    addOns: [
      { id: 'marcus-hour', name: 'Extra service hour', description: 'Stay to carve seconds.', price: 800, unit: 'per_hour' },
      { id: 'marcus-dessert', name: 'Dessert tray', description: 'Egg tarts and mango pudding for twelve.', price: 1100, unit: 'flat' },
      { id: 'marcus-server', name: 'Server for the night', description: 'Plating, refills, clearing.', price: 3200, unit: 'flat' },
      { id: 'marcus-travel', name: 'Travel beyond area', description: 'Charged per km past 12 km.', price: 18, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'ma1', name: 'Old town & Yaowarat', districts: ['Yaowarat', 'Talat Noi', 'Phra Nakhon'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.7405, lng: 100.5095, radiusKm: 4 },
      { id: 'ma2', name: 'Sathorn & Silom', districts: ['Silom', 'Sathorn'], fulfillment: ['delivery'], travelIncluded: true, lat: 13.724, lng: 100.531, radiusKm: 3.5 },
    ],
    gallery: [
      { id: 'mg1', url: IMG.dishMezze, caption: 'Braised goose, cold plate', tag: 'dish' },
      { id: 'mg2', url: IMG.cooking, caption: 'Folding har gow', tag: 'process' },
      { id: 'mg3', url: IMG.heroTable, caption: 'Sunday, twenty people', tag: 'table' },
    ],
    verifications: verifiedSet({ background: false }),
  },
  {
    id: 'chef-sofia',
    slug: 'linh-tran',
    name: 'Linh Tran',
    photo: IMG.chefLinh,
    headline: 'Northern Vietnamese cooking with a French hand, herbs by the armful',
    bio: 'Hanoi born, trained in a French kitchen in Saigon. I cook bún, broths that simmer all afternoon, and a lot of raw herbs — plus a pâté and a baguette, because that is also Vietnamese food. Menus lean light and vegetable-first with fish when it makes sense.',
    cuisineIds: ['vietnamese', 'french', 'plant'],
    languages: ['Vietnamese', 'English', 'French', 'Thai'],
    fulfillment: ['in_home', 'delivery'],
    city: 'Bangkok',
    neighbourhood: 'Phrom Phong',
    lat: 13.7305,
    lng: 100.5695,
    rating: 4.8,
    reviewCount: 44,
    bookingsCompleted: 61,
    responseMinutes: 68,
    acceptanceRate: 83,
    repeatRate: 35,
    bookingMode: 'request',
    yearsPro: 6,
    joinedOn: '2025-03-06',
    nextAvailable: '2026-09-08',
    minimumSpend: 3600,
    travelIncludedKm: 8,
    travelPerKm: 22,
    depositPercent: 30,
    cancellationTier: 'moderate',
    dietaryCapabilities: ['Jay (Buddhist vegan) menus', 'Gluten-free menus', 'Low-sodium', 'No fish sauce or shrimp paste'],
    deliveryFee: 190,
    deliveryFreeRadiusKm: 5,
    minDeliveryOrder: 1500,
    menus: [
      {
        id: 'menu-sofia-harvest',
        name: 'Hanoi table',
        tagline: 'A broth that took all afternoon, and herbs by the armful',
        cuisineIds: ['vietnamese', 'french'],
        fulfillment: ['in_home'],
        pricePerGuest: 1320,
        minGuests: 2,
        includedHours: 3.5,
        courses: [
          { name: 'To start', description: 'Chicken liver pâté, pickles, warm baguette' },
          { name: 'Fresh', description: 'Nem cuốn rolled at the table, herb plate' },
          { name: 'Broth', description: 'Bún chả cá with dill and turmeric' },
          { name: 'Sweet', description: 'Coconut caramel flan' },
        ],
        groceries: 'included',
        signature: true,
        image: IMG.dishDessert,
        equipmentNeeded: ['large_stockpot', 'heavy_skillet', 'sharp_knives'],
        dietaryTags: ['Pescatarian', 'Vegan on request'],
        leadTimeDays: 4,
      },
      {
        id: 'menu-sofia-fika',
        name: 'Bánh mì & herbs delivery',
        tagline: 'Baguettes, pâté and pickles dropped at your door',
        cuisineIds: ['vietnamese'],
        fulfillment: ['delivery'],
        pricePerGuest: 420,
        pricePerPortion: 260,
        minGuests: 2,
        includedHours: 0,
        courses: [
          { name: 'Baked', description: 'Baguettes baked that morning' },
          { name: 'Jars', description: 'Pâté, pickled carrot and daikon, chilli butter' },
        ],
        groceries: 'included',
        signature: false,
        image: IMG.dishMango,
        equipmentNeeded: [],
        dietaryTags: ['Vegetarian option'],
        leadTimeDays: 2,
      },
    ],
    addOns: [
      { id: 'sofia-hour', name: 'Extra service hour', description: 'A longer, slower table.', price: 950, unit: 'per_hour' },
      { id: 'sofia-bread', name: 'Take-home baguette per guest', description: 'Baked that morning, wrapped in paper.', price: 120, unit: 'per_guest' },
      { id: 'sofia-snaps', name: 'Coffee & rice wine pairing', description: 'Three pours plus cà phê sữa đá.', price: 480, unit: 'per_guest' },
      { id: 'sofia-travel', name: 'Travel beyond area', description: 'Charged per km past 8 km.', price: 22, unit: 'per_km' },
    ],
    serviceAreas: [
      { id: 'sa1', name: 'Sukhumvit mid', districts: ['Phrom Phong', 'Thong Lo', 'Asok'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.7305, lng: 100.5695, radiusKm: 3.5 },
      { id: 'sa2', name: 'On Nut & Phra Khanong', districts: ['On Nut', 'Phra Khanong', 'Ekkamai'], fulfillment: ['in_home', 'delivery'], travelIncluded: true, lat: 13.7055, lng: 100.6015, radiusKm: 3 },
    ],
    gallery: [
      { id: 'sg1', url: IMG.dishDessert, caption: 'Coconut caramel flan', tag: 'dish' },
      { id: 'sg2', url: IMG.heroTable, caption: 'Hanoi table for six', tag: 'table' },
      { id: 'sg3', url: IMG.cooking, caption: 'Charring for the broth', tag: 'process' },
    ],
    verifications: verifiedSet({ insurancePending: true, background: false }),
  },
];

export function chefById(id: string): Chef | undefined {
  return chefs.find((c) => c.id === id || c.slug === id);
}

/** The chef whose studio the chef-side screens edit. */
export const currentChefId = 'chef-elena';
